import type { Backend } from './backends';

function field(body: unknown, key: string): unknown {
    return typeof body === 'object' && body !== null && key in body
        ? (body as Record<string, unknown>)[key]
        : undefined;
}

export class ApiError extends Error {
    readonly status: number;
    readonly body: unknown;
    /** BE error `name` (e.g. 'Conflict', 'FinInsteadOfVin'); null when the body carries none. */
    readonly errorName: string | null;
    /** BE error `details` (validation messages, `{ vinForFin }`, …). */
    readonly details: unknown;

    constructor(status: number, message: string, body: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
        const name = field(body, 'name');
        this.errorName = typeof name === 'string' ? name : null;
        this.details = field(body, 'details');
    }
}

export interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    query?: Record<string, string | number | boolean | undefined>;
    body?: unknown;
}

interface ClientConfig {
    getToken: () => string | null;
    onUnauthorized: () => void;
}

let config: ClientConfig = { getToken: () => null, onUnauthorized: () => {} };

/** The session owner (src/auth/session.ts) registers how to read the token and what to do on 401. */
export function configureClient(next: ClientConfig): void {
    config = next;
}

function parseJson(text: string): unknown {
    try {
        return JSON.parse(text) as unknown;
    } catch {
        return text;
    }
}

function messageOf(body: unknown, fallback: string): string {
    const message = field(body, 'message');
    return typeof message === 'string' ? message : fallback;
}

export async function request<T>(backend: Backend, path: string, options: RequestOptions = {}): Promise<T> {
    const url = new URL(backend + path);
    for (const [key, value] of Object.entries(options.query ?? {})) {
        if (value !== undefined) {
            url.searchParams.set(key, String(value));
        }
    }

    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }
    const token = config.getToken();
    if (token !== null) {
        headers.Authorization = `Bearer ${token}`;
    }

    let response: Response;
    try {
        response = await fetch(url, {
            method: options.method ?? 'GET',
            headers,
            body: options.body === undefined ? undefined : JSON.stringify(options.body),
        });
    } catch (cause) {
        throw new ApiError(0, 'Network error', cause);
    }

    const text = await response.text();
    const body: unknown = text ? parseJson(text) : null;

    if (response.ok) {
        return body as T;
    }
    if (response.status === 401 && token !== null) {
        config.onUnauthorized();
    }
    throw new ApiError(response.status, messageOf(body, response.statusText), body);
}
